// src/gen/solver.ts
//
// Forward search over Color Slide game states (§4.1 / §12 of the canonical
// contract). This module is the INDEPENDENT solvability cross-check: it knows
// nothing about how levels are generated and derives everything from the public
// rules engine surface (`rules.ts`). It powers (a) obstacle-level validation and
// (b) the Hint button.
//
// Strategy: breadth-first search. BFS visits every reachable state at its
// minimal ply, so the first time the goal is generated it is via a SHORTEST move
// sequence (invariant S1: BFS-optimal). Successors come from `legalMoves`, whose
// canonical ordering (blockId asc, then DIRECTIONS order) is fixed, so ties break
// deterministically and the whole search is a pure function of (state, options)
// (invariant S2) — modulo the wall-clock safety cap, which only ever aborts a
// search that was already over budget.
//
// The search is bounded by three independent budgets (depth, node-expansions,
// wall-clock) so it always terminates, which is what makes it safe to wire to a
// Hint button.

import { applyMove, hashState, isSolved, legalMoves } from '../game/rules';
import type { GameState } from '../game/rules';
import { gateInnerCells } from '../game/board';
import { blockBounds } from '../game/block';
import type { Cell, Color, Move } from '../game/types';

export interface SolverOptions {
  readonly maxDepth?: number; // IDDFS depth / BFS ply cap. Omit ⇒ default 64.
  readonly maxNodes?: number; // node-expansion budget. Omit ⇒ default 200_000.
  readonly maxMillis?: number; // wall-clock budget ms. Omit ⇒ default 1_000.
}

export type SolverStatus = 'solved' | 'unsolvable' | 'budget-exceeded';

export interface SolveResult {
  readonly status: SolverStatus;
  /** Non-null IFF status === 'solved'. Use null, never undefined. */
  readonly solution: readonly Move[] | null;
  readonly nodesExpanded: number;
  readonly maxDepthReached: number;
}

const DEFAULT_MAX_DEPTH = 64;
const DEFAULT_MAX_NODES = 200_000;
const DEFAULT_MAX_MILLIS = 1_000;

/** Sample the clock every this-many expansions (keeps Date.now() overhead off
 *  the hot path and makes budget-exceeded behaviour stable run-to-run). */
const TIME_CHECK_INTERVAL = 1_024;

/**
 * A node in the BFS frontier. Parent pointers (indices into a flat `nodes`
 * array) let us reconstruct the move path without storing a copy of the move
 * list in every node.
 */
interface SearchNode {
  readonly state: GameState;
  readonly parent: number; // index into the nodes array; -1 for the root.
  readonly move: Move | null; // move that produced `state` from its parent; null at root.
  readonly depth: number; // ply from the start state (root === 0).
}

/**
 * Forward BFS over GameState with a `hashState()` visited-set (§4.1, §12).
 * Returns the shortest move sequence that clears the board, or a non-'solved'
 * status if none is found within the supplied budgets.
 *
 *  - 'solved'          : `solution` replays forward to an empty board (S1).
 *  - 'unsolvable'      : the ENTIRE reachable state space (within maxDepth) was
 *                        explored with no solution and no budget pruned the
 *                        search — the puzzle genuinely has no solution.
 *  - 'budget-exceeded' : a budget (depth / nodes / time) stopped the search
 *                        before it could be proven solvable or unsolvable.
 *
 * Read-only & deterministic in (state, options). Never mutates `state`.
 */
export function solve(state: GameState, options?: SolverOptions): SolveResult {
  const maxDepth = options?.maxDepth ?? DEFAULT_MAX_DEPTH;
  const maxNodes = options?.maxNodes ?? DEFAULT_MAX_NODES;
  const maxMillis = options?.maxMillis ?? DEFAULT_MAX_MILLIS;
  const startedAt = Date.now();

  let nodesExpanded = 0;
  let maxDepthReached = 0;

  // Already solved: the empty move sequence clears the board.
  if (isSolved(state)) {
    return { status: 'solved', solution: [], nodesExpanded, maxDepthReached };
  }

  // A non-positive depth/node budget can never reach a (necessarily deeper) goal.
  if (maxDepth < 1 || maxNodes < 1) {
    return { status: 'budget-exceeded', solution: null, nodesExpanded, maxDepthReached };
  }

  const nodes: SearchNode[] = [{ state, parent: -1, move: null, depth: 0 }];
  const queue: number[] = [0]; // FIFO of indices into `nodes`, consumed via `head`.
  const visited = new Set<string>();
  visited.add(hashState(state));

  let depthLimited = false; // true once the depth cap prunes a would-be successor.
  let head = 0;

  while (head < queue.length) {
    const idx = queue[head];
    head += 1;
    if (idx === undefined) {
      continue; // unreachable (head < length), but satisfies noUncheckedIndexedAccess.
    }
    const node = nodes[idx];
    if (node === undefined) {
      continue;
    }

    // Wall-clock budget (sampled to keep overhead low and the result stable).
    if (nodesExpanded % TIME_CHECK_INTERVAL === 0 && Date.now() - startedAt > maxMillis) {
      return { status: 'budget-exceeded', solution: null, nodesExpanded, maxDepthReached };
    }

    // Node-expansion budget: expand at most `maxNodes` nodes.
    if (nodesExpanded >= maxNodes) {
      return { status: 'budget-exceeded', solution: null, nodesExpanded, maxDepthReached };
    }
    nodesExpanded += 1;

    const childDepth = node.depth + 1;
    const moves = legalMoves(node.state);
    for (const move of moves) {
      // Every successor of this node shares `childDepth`; if that exceeds the
      // cap, none of them can be enqueued — record the truncation and stop.
      if (childDepth > maxDepth) {
        depthLimited = true;
        break;
      }

      // legalMoves yields only non-noop moves, so applyMove always advances to a
      // genuinely new state.
      const next = applyMove(node.state, move).state;
      const key = hashState(next);
      if (visited.has(key)) {
        continue;
      }

      if (childDepth > maxDepthReached) {
        maxDepthReached = childDepth;
      }

      if (isSolved(next)) {
        const solution = reconstructPath(nodes, idx, move);
        return { status: 'solved', solution, nodesExpanded, maxDepthReached };
      }

      visited.add(key);
      nodes.push({ state: next, parent: idx, move, depth: childDepth });
      queue.push(nodes.length - 1);
    }
  }

  // Frontier drained. If the depth cap pruned any branch the search was
  // incomplete (a longer solution may still exist) ⇒ budget-exceeded; otherwise
  // every reachable state was examined ⇒ genuinely unsolvable.
  return {
    status: depthLimited ? 'budget-exceeded' : 'unsolvable',
    solution: null,
    nodesExpanded,
    maxDepthReached,
  };
}

/**
 * Walk parent pointers from the node that produced the goal back to the root,
 * collecting moves, then return them start→goal order. `finalMove` is the move
 * applied to `nodes[parentIdx]` that reached the goal.
 */
function reconstructPath(
  nodes: readonly SearchNode[],
  parentIdx: number,
  finalMove: Move,
): Move[] {
  const reversed: Move[] = [finalMove];
  let cur = parentIdx;
  while (cur !== -1) {
    const node = nodes[cur];
    if (node === undefined) {
      break; // unreachable; satisfies noUncheckedIndexedAccess.
    }
    if (node.move !== null) {
      reversed.push(node.move);
    }
    cur = node.parent;
  }
  reversed.reverse();
  return reversed;
}

/**
 * Hint helper (§4.1, §12). Unlike `solve` (optimal BFS, which doesn't scale to the larger boards),
 * this is a GREEDY BEST-FIRST search guided by a heuristic (fewer blocks left + blocks nearer their
 * gates). It returns the FIRST move of a found solution; if no full solution turns up within budget,
 * it falls back to the first move that led toward the most-progress (lowest-heuristic) state explored.
 * So it always returns a genuinely helpful move whenever any non-noop move exists — at any board size.
 */
export function nextHintMove(state: GameState, options?: SolverOptions): Move | null {
  if (isSolved(state)) {
    return null;
  }
  const maxNodes = options?.maxNodes ?? 30_000;
  const maxMillis = options?.maxMillis ?? 80;
  const startedAt = Date.now();

  const targets = gateTargets(state);
  const h = (s: GameState): number => heuristic(s, targets);

  const visited = new Set<string>([hashState(state)]);
  const heap = new MinHeap();
  let bestMove: Move | null = null;
  let bestH = Infinity;

  const consider = (next: GameState, first: Move): Move | 'continue' | 'enqueue' => {
    const key = hashState(next);
    if (visited.has(key)) {
      return 'continue';
    }
    visited.add(key);
    if (isSolved(next)) {
      return first; // a real solution — its first move is the hint
    }
    const hv = h(next);
    if (hv < bestH) {
      bestH = hv;
      bestMove = first;
    }
    heap.push({ state: next, first, h: hv });
    return 'enqueue';
  };

  for (const m of legalMoves(state)) {
    const r = consider(applyMove(state, m).state, m);
    if (r !== 'continue' && r !== 'enqueue') {
      return r;
    }
  }

  let nodes = 0;
  let timedOut = false;
  while (!heap.isEmpty() && nodes < maxNodes) {
    if ((nodes & 1023) === 0 && Date.now() - startedAt > maxMillis) {
      timedOut = true;
      break;
    }
    nodes += 1;
    const node = heap.pop();
    for (const m of legalMoves(node.state)) {
      const r = consider(applyMove(node.state, m).state, node.first);
      if (r !== 'continue' && r !== 'enqueue') {
        return r;
      }
    }
  }
  // Heap drained without timing out or hitting the node cap ⇒ the whole reachable space was explored
  // and there is genuinely no solution ⇒ no hint. Otherwise (budget ran out) offer the best-progress
  // move — which, on the game's always-solvable levels, is what actually gets returned.
  if (heap.isEmpty() && !timedOut && nodes < maxNodes) {
    return null;
  }
  return bestMove;
}

interface HeapItem {
  readonly state: GameState;
  readonly first: Move;
  readonly h: number;
}

/** Tiny binary min-heap keyed by `h` (greedy best-first frontier). */
class MinHeap {
  private readonly a: HeapItem[] = [];

  isEmpty(): boolean {
    return this.a.length === 0;
  }

  push(item: HeapItem): void {
    const a = this.a;
    a.push(item);
    let i = a.length - 1;
    while (i > 0) {
      const p = (i - 1) >> 1;
      if (a[p]!.h <= a[i]!.h) {
        break;
      }
      [a[p], a[i]] = [a[i]!, a[p]!];
      i = p;
    }
  }

  pop(): HeapItem {
    const a = this.a;
    const top = a[0]!;
    const last = a.pop()!;
    if (a.length > 0) {
      a[0] = last;
      const n = a.length;
      let i = 0;
      for (;;) {
        const l = 2 * i + 1;
        const r = 2 * i + 2;
        let s = i;
        if (l < n && a[l]!.h < a[s]!.h) {
          s = l;
        }
        if (r < n && a[r]!.h < a[s]!.h) {
          s = r;
        }
        if (s === i) {
          break;
        }
        [a[s], a[i]] = [a[i]!, a[s]!];
        i = s;
      }
    }
    return top;
  }
}

/**
 * Map each gate colour to the midpoint cell of its inner run.
 * For a span-1 gate this is identical to the old gateInnerCell; for wider gates
 * it aims at the centre of the opening rather than the first covered cell.
 */
function gateTargets(state: GameState): Map<Color, Cell> {
  const targets = new Map<Color, Cell>();
  for (const gate of state.board.gates) {
    const cells = gateInnerCells(state.board, gate);
    // Take the middle cell of the run (floor for even-length spans).
    const mid = cells[Math.floor((cells.length - 1) / 2)];
    if (mid !== undefined) {
      targets.set(gate.color, mid);
    }
  }
  return targets;
}

/**
 * Lower is closer to solved: blocks-remaining dominates; ties broken by total
 * distance from each block's shape centroid (bounding-box centre) to its gate's
 * inner-run midpoint. Using the centroid instead of the raw anchor produces a
 * more accurate distance for non-square / off-centre polyominos.
 */
function heuristic(state: GameState, targets: Map<Color, Cell>): number {
  let dist = 0;
  for (const b of state.blocks) {
    const t = targets.get(b.color);
    if (t !== undefined) {
      const bounds = blockBounds(b);
      const cy = (bounds.min.row + bounds.max.row) / 2;
      const cx = (bounds.min.col + bounds.max.col) / 2;
      dist += Math.abs(cy - t.row) + Math.abs(cx - t.col);
    }
  }
  return state.blocks.length * 1000 + dist;
}
